import { isNavigationFailure } from '~/utils/analytics/isNavigationFailure';
import { extractTopicData } from '~/utils/analytics/extractTopicData';
import {
  useCurrentPageState,
  useTopicSlugMapEn
} from '~/composables/useCurrentPageTopics';
import { buildUrlString } from '~/utils/buildUrlString';

declare global {
  type EventInfoModel = {
    category: string;
    action: string;
    label: string;
  };

  type AnalyticsEventModel =
    | {
        event: 'customEvent' | string;
        eventInfo: EventInfoModel;
      }
    | object;
}

export default defineNuxtPlugin({
  name: 'analytics',
  setup() {
    const router = useRouter();
    const { $shopStore, $localeSlugMap } = useNuxtApp();
    const { user } = useOidcAuth();
    const locale = useLocale();
    const currentPageState = useCurrentPageState();
    const topicSlugMapEn = useTopicSlugMapEn();

    // inspired by https://github.com/gtm-support/vue-gtm/blob/main/src/index.ts
    router.afterEach((to, from, failure) => {
      // duplicate route navigation? ignore.
      if (to.fullPath === from.fullPath) {
        return;
      }
      if (typeof to.name !== 'string') {
        return;
      }

      if (
        isNavigationFailure(failure, 4 /* NAVIGATION_ABORTED */) ||
        isNavigationFailure(failure, 8 /* NAVIGATION_CANCELLED */)
      ) {
        return;
      }

      // inspired by https://github.com/MatteoGabriele/vue-gtag/blob/master/src/add-routes-tracker.js
      return nextTick(() => {
        analytics.pushToDataLayer({ page_title: document.title });
        analytics.pushToDataLayer({ page_path: to.fullPath });
        analytics.pushToDataLayer({ ecom_region: $shopStore.value });
        analytics.pushToDataLayer({
          user_id: user?.value?.claims?.oid ?? 'null'
        });

        // Push page format if available
        if (currentPageState.value?.fields?.format) {
          analytics.pushToDataLayer({
            page_format: currentPageState.value.fields.format
          });
        }

        // Topic tracking is handled by the watch below when page data is available
      });
    });

    const analytics = {
      pushToDataLayer(event: AnalyticsEventModel) {
        window.dataLayer?.push(event);
      },
      getReferrer: () => {
        // this seeems to be undocumented. Probably unstable?
        // @see https://stackoverflow.com/questions/53788975/vue-router-how-to-get-previous-page-url
        return router.options.history.state.back?.toString();
      }
    };

    watch(
      [() => $localeSlugMap.value?.en],
      (next, prev) => {
        const nextValue = next?.[0];
        if (prev?.[0] === nextValue) {
          return;
        }
        if (nextValue) {
          // Push English path
          analytics?.pushToDataLayer({
            page_path_en: buildUrlString('en', nextValue)
          });

          // Push topic data if current page has topics
          if (currentPageState.value) {
            const topicData = extractTopicData(
              currentPageState.value,
              locale.value as any,
              topicSlugMapEn.value
            );

            if (
              topicData.topic_slugs.length > 0 ||
              topicData.topic_ids.length > 0 ||
              topicData.topic_slugs_en.length > 0
            ) {
              const topicPayload: Record<string, any> = {};

              if (topicData.topic_slugs.length > 0) {
                topicPayload.topic_slugs = topicData.topic_slugs;
              }
              if (topicData.topic_slugs_en.length > 0) {
                topicPayload.topic_slugs_en = topicData.topic_slugs_en;
              }
              if (topicData.topic_ids.length > 0) {
                topicPayload.topic_ids = topicData.topic_ids;
              }

              analytics?.pushToDataLayer(topicPayload);
            }
          }
        }
      },
      { immediate: true }
    );

    return {
      provide: {
        analytics
      }
    };
  }
});
